import { useEffect, useRef } from "react";

/**
 * useAxisLockedSwipe — non-passive touch handler that ONLY captures horizontal
 * swipes. Vertical motion is detected on the very first move and the gesture
 * is abandoned, letting the document handle the vertical scroll natively.
 *
 * Why this exists:
 *   CSS `touch-action: pan-x` should in theory let vertical pans bubble to the
 *   page, but in practice (Chrome Android, certain iOS Safari versions) the
 *   browser frequently mis-locks the gesture into horizontal-only when the
 *   user does a slightly-diagonal vertical swipe — vertical scroll appears to
 *   "freeze" until the user lifts and re-touches. This is the user-visible
 *   bug that's bitten us repeatedly. We can't ship around CSS quirks, so we
 *   implement axis lock manually with a small bias toward vertical (the user
 *   intent that the page should ALWAYS scroll wins on tie).
 *
 * Usage:
 *   const { onTouchStart, onTouchMove, onTouchEnd } = useAxisLockedSwipe({
 *     onSwipe: (dir) => goTo(active + dir),  // dir = -1 | +1
 *     elementRef: trackRef,                  // we'll attach a non-passive
 *                                            // listener via this ref so
 *                                            // preventDefault() works.
 *   });
 *
 * Behaviour:
 *   - Decide axis at the first move >6px from start. 
 *   - If |dy| >= |dx|, lock vertical and stop tracking (page scrolls).
 *   - If |dx| > |dy|, lock horizontal, preventDefault on subsequent moves so
 *     the browser doesn't try to scroll the carousel natively, and call
 *     onDrag(dx) on each move + onSwipe(dir) at the end if dx > threshold.
 */
export default function useAxisLockedSwipe({
  elementRef,
  onSwipe,
  onDrag,
  threshold = 50,
  velocityThreshold = 0.35, // px/ms
}) {
  const stateRef = useRef({
    startX: 0,
    startY: 0,
    startTime: 0,
    locked: null, // 'h' | 'v' | null
    lastDx: 0,
  });

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;
    const s = stateRef;

    const onStart = (ev) => {
      const t = ev.touches[0];
      s.current = {
        startX: t.clientX,
        startY: t.clientY,
        startTime: Date.now(),
        locked: null,
        lastDx: 0,
      };
    };

    const onMove = (ev) => {
      const t = ev.touches[0];
      if (!t) return;
      const dx = t.clientX - s.current.startX;
      const dy = t.clientY - s.current.startY;

      if (s.current.locked === null) {
        // First decisive movement (>6px in either axis) decides the axis.
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        // Tie / vertical-dominant → bail out. Don't preventDefault, don't
        // capture future moves: the page should scroll naturally.
        if (Math.abs(dy) >= Math.abs(dx)) {
          s.current.locked = "v";
          return;
        }
        s.current.locked = "h";
      }

      if (s.current.locked === "h") {
        // We own the horizontal gesture — stop the browser from doing
        // anything else (history-back swipe, native scroll, etc.).
        if (ev.cancelable) ev.preventDefault();
        s.current.lastDx = dx;
        if (onDrag) onDrag(dx);
      }
    };

    const onEnd = () => {
      if (s.current.locked === "h" && onSwipe) {
        const dx = s.current.lastDx;
        const dt = Date.now() - s.current.startTime;
        const velocity = Math.abs(dx) / Math.max(1, dt);
        if (Math.abs(dx) > threshold || velocity > velocityThreshold) {
          onSwipe(dx > 0 ? -1 : +1);
        }
        // ALWAYS reset drag to 0 — whether the gesture advanced the index or
        // snapped back. Without this the track stays offset by the last
        // drag-px, so the card visibly rests off-centre.
        if (onDrag) onDrag(0);
      } else if (s.current.locked === "h" && onDrag) {
        onDrag(0);
      }
      s.current.locked = null;
      s.current.lastDx = 0;
    };

    // passive: false is REQUIRED for preventDefault inside touchmove. React's
    // synthetic event system attaches passive listeners by default, so we
    // sidestep React for these three events.
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: true });
    el.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [elementRef, onSwipe, onDrag, threshold, velocityThreshold]);
}
